import React, { useCallback } from 'react';
import { Option, Select } from 'carbon-react/lib/components/select';

import { useIntl } from '@futrli/localisation';
import { ListingPageFilterBar } from '@futrli/ui-components/components/listing-page/listing-page-filter-bar/listing-page-filter-bar';

import { SortOption } from '../../../../utils/list-filters/list-filter-utils';
import { StatusOrientationFilters } from '../shared/status-orientation-filters';
import { StatusFilterValue, TypeFilterValue } from '../shared/use-status-orientation-filter';

interface ReportTemplatesFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: StatusFilterValue;
  onStatusChange: (value: StatusFilterValue) => void;
  typeFilter: TypeFilterValue;
  onTypeChange: (value: TypeFilterValue) => void;
  sortValue: string;
  onSortChange: (value: string) => void;
  sortOptions: SortOption[];
  stickyTop?: number;
  isResourceSharingEnabled?: boolean;
}

/**
 * Filter header for the Report Templates tab.
 * Identical layout to My Reports — composes ListingPageFilterBar with StatusOrientationFilters.
 * Status toggle maps 'published'/'draft' to template status values.
 */
export const ReportTemplatesFilters = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  typeFilter,
  onTypeChange,
  sortValue,
  onSortChange,
  sortOptions,
  stickyTop,
  isResourceSharingEnabled = false
}: ReportTemplatesFiltersProps) => {
  const intl = useIntl();

  const handleSortChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSortChange(event.target.value);
    },
    [onSortChange]
  );

  return (
    <ListingPageFilterBar
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      stickyTop={stickyTop}
      rightContent={
        <div style={{ minWidth: 250 }}>
          <Select
            id="report-templates-sort-by"
            value={sortValue}
            onChange={handleSortChange}
            size="medium"
            label={intl.formatMessage({ id: 'predict.sort_by', defaultMessage: 'Sort by' })}
            aria-label={intl.formatMessage({ id: 'predict.sort_by', defaultMessage: 'Sort by' })}
          >
            {sortOptions.map(opt => (
              <Option key={opt.value} value={opt.value} text={opt.label} />
            ))}
          </Select>
        </div>
      }
    >
      <StatusOrientationFilters
        statusFilter={statusFilter}
        onStatusChange={onStatusChange}
        typeFilter={typeFilter}
        onTypeChange={onTypeChange}
        isResourceSharingEnabled={isResourceSharingEnabled}
      />
    </ListingPageFilterBar>
  );
};
